import { useState } from 'react';
import { useDevices, useActivationCodes, useCreateActivationCode, useUpdateDevice, useDeleteDevice, useResetDevice } from '../../hooks/useDevices';
import { useAuthStore } from '../../stores/auth.store';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { formatDateTime, getRelativeTime } from '../../lib/utils';

export default function DevicesPage() {
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';

  const { data: devices, isLoading: loadingDevices } = useDevices();
  const { data: codes, isLoading: loadingCodes } = useActivationCodes();
  const { mutate: createCode, isPending: creatingCode } = useCreateActivationCode();
  const { mutate: updateDevice } = useUpdateDevice();
  const { mutate: deleteDevice } = useDeleteDevice();
  const { mutate: resetDevice, isPending: resettingDevice, variables: resettingVars } = useResetDevice();

  const [showCodes, setShowCodes] = useState(false);

  const handleCreateCode = () => {
    createCode({ expiresInHours: 24 });
  };

  const handleToggleDevice = (device: any) => {
    updateDevice({ id: device.id, data: { isActive: !device.isActive } });
  };

  const handleDeleteDevice = (device: any) => {
    const message = device.isActive
      ? `Delete "${device.name}"? This remotely uninstalls Apace Pulse from that computer first — ` +
        `the device will disappear from this list once it confirms, which is near-instant if it's ` +
        `online now, or on its next check-in otherwise.`
      : `Delete "${device.name}"? This device is already disabled, so it will be removed immediately.`;
    if (window.confirm(message)) {
      deleteDevice({ id: device.id });
    }
  };

  const handleForceDeleteDevice = (device: any) => {
    if (
      window.confirm(
        `Force delete "${device.name}"? Use this only if the device is gone for good (lost, wiped, ` +
          `stolen) and will never check in again. This removes the device record immediately without ` +
          `waiting for — or confirming — a remote uninstall, so Apace Pulse may still be left running ` +
          `on that computer if it ever reconnects.`
      )
    ) {
      deleteDevice({ id: device.id, force: true });
    }
  };

  const handleResetDevice = (device: any) => {
    if (
      window.confirm(
        `Reset "${device.name}"? This remotely uninstalls Apace Pulse from that computer. ` +
          `${device.user?.firstName || 'The employee'} will be able to reinstall it from their dashboard afterward. ` +
          `Historical screenshots and activity for this device are kept.`
      )
    ) {
      resetDevice(device.id);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Devices</h1>
          <p className="text-gray-600">Manage tracked devices</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowCodes(!showCodes)}>
              {showCodes ? 'Hide Codes' : 'Show Codes'}
            </Button>
            <Button onClick={handleCreateCode} disabled={creatingCode}>
              {creatingCode ? 'Creating...' : 'Generate Code'}
            </Button>
          </div>
        )}
      </div>

      {/* Activation Codes */}
      {isAdmin && showCodes && (
        <Card>
          <CardHeader>
            <CardTitle>Activation Codes</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingCodes ? (
              <p className="text-gray-500">Loading...</p>
            ) : codes?.length === 0 ? (
              <p className="text-gray-500">No activation codes generated yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-4">Code</th>
                      <th className="text-left py-2 px-4">Status</th>
                      <th className="text-left py-2 px-4">Created</th>
                      <th className="text-left py-2 px-4">Expires</th>
                      <th className="text-left py-2 px-4">Created By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {codes?.map((code: any) => {
                      const isExpired = new Date(code.expiresAt) < new Date();
                      const status = code.usedAt ? 'Used' : isExpired ? 'Expired' : 'Active';
                      const statusColor =
                        status === 'Active'
                          ? 'text-green-600 bg-green-50'
                          : status === 'Used'
                          ? 'text-blue-600 bg-blue-50'
                          : 'text-red-600 bg-red-50';

                      return (
                        <tr key={code.id} className="border-b last:border-0">
                          <td className="py-2 px-4">
                            <code className="font-mono bg-gray-100 px-2 py-1 rounded">
                              {code.code}
                            </code>
                          </td>
                          <td className="py-2 px-4">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${statusColor}`}>
                              {status}
                            </span>
                          </td>
                          <td className="py-2 px-4 text-gray-500">
                            {formatDateTime(code.createdAt)}
                          </td>
                          <td className="py-2 px-4 text-gray-500">
                            {formatDateTime(code.expiresAt)}
                          </td>
                          <td className="py-2 px-4 text-gray-500">
                            {code.createdBy?.firstName} {code.createdBy?.lastName}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Devices List */}
      <Card>
        <CardHeader>
          <CardTitle>{devices?.length || 0} Devices</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingDevices ? (
            <p className="text-gray-500">Loading...</p>
          ) : devices?.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">No devices registered yet</p>
              {isAdmin && (
                <p className="text-sm text-gray-400">
                  Generate an activation code and use it to register a device
                </p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-4">Device</th>
                    <th className="text-left py-2 px-4">User</th>
                    <th className="text-left py-2 px-4">Status</th>
                    <th className="text-left py-2 px-4">Last Seen</th>
                    <th className="text-left py-2 px-4">Platform</th>
                    <th className="text-left py-2 px-4">Version</th>
                    {isAdmin && <th className="text-left py-2 px-4">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {devices?.map((device: any) => {
                    const isOnline =
                      device.lastSeenAt &&
                      new Date(device.lastSeenAt) > new Date(Date.now() - 5 * 60 * 1000);

                    return (
                      <tr key={device.id} className="border-b last:border-0">
                        <td className="py-3 px-4">
                          <div className="flex items-center">
                            <div
                              className={`w-2 h-2 rounded-full mr-3 ${
                                isOnline ? 'bg-green-500' : 'bg-gray-300'
                              }`}
                            />
                            <div>
                              <p className="font-medium">{device.name}</p>
                              <p className="text-xs text-gray-500">{device.hostname}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {device.user?.firstName} {device.user?.lastName}
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              device.isActive
                                ? 'text-green-600 bg-green-50'
                                : 'text-red-600 bg-red-50'
                            }`}
                          >
                            {device.isActive ? 'Active' : 'Disabled'}
                          </span>
                          {device.pendingUninstall && (
                            <>
                              <span
                                className="ml-2 px-2 py-1 rounded text-xs font-medium text-amber-600 bg-amber-50"
                                title="Waiting for the device to confirm it has uninstalled Apace Pulse"
                              >
                                Uninstalling…
                              </span>
                              <p className="mt-1 text-xs text-gray-400">
                                Refresh the page to see the latest status
                              </p>
                            </>
                          )}
                        </td>
                        <td className="py-3 px-4 text-gray-500">
                          {device.lastSeenAt ? getRelativeTime(device.lastSeenAt) : 'Never'}
                        </td>
                        <td className="py-3 px-4 text-gray-500 capitalize">{device.platform}</td>
                        <td className="py-3 px-4 text-gray-500">{device.appVersion || '—'}</td>
                        {isAdmin && (
                          <td className="py-3 px-4">
                            <div className="flex gap-2">
                              {device.isActive && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-amber-600 hover:text-amber-700"
                                  disabled={resettingDevice && resettingVars === device.id}
                                  onClick={() => handleResetDevice(device)}
                                  title="Remotely uninstall Apace Pulse from this device"
                                >
                                  {resettingDevice && resettingVars === device.id
                                    ? 'Resetting...'
                                    : 'Reset'}
                                </Button>
                              )}
                              {device.uninstalled ? (
                                <span
                                  className="px-2 py-1 text-xs text-gray-400"
                                  title="Apace Pulse was uninstalled from this computer. Reinstalling always creates a new device entry — this one can only be deleted, not reactivated."
                                >
                                  Uninstalled
                                </span>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleToggleDevice(device)}
                                  title={
                                    device.isActive
                                      ? 'Immediately revoke access without waiting for the device to confirm — use if the machine is offline or gone'
                                      : undefined
                                  }
                                >
                                  {device.isActive ? 'Disable' : 'Enable'}
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteDevice(device)}
                              >
                                Delete
                              </Button>
                              {device.pendingUninstall && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700"
                                  onClick={() => handleForceDeleteDevice(device)}
                                  title="Delete immediately without waiting for the device to confirm uninstall — use only if it's gone for good"
                                >
                                  Force Delete
                                </Button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
